/**
 * lib/interimPolicy.js — Phase 1 containment ONLY. Mirror of
 * backend/lib/interim_policy.py; keep the two category sets in sync.
 *
 * DELETION CONTRACT: this module is temporary. It exists so the financial
 * exclusions the product already claims to make live in ONE place instead of
 * four. No other frontend file may express a financial exclusion — if you are
 * about to write `if (t.category === ...)` to keep something out of a total,
 * put it here. Removed in Phase 4 when the versioned metrics API takes over
 * (see docs/METRIC_CONTRACTS.md); its removal is a release gate.
 *
 * It adds NO new rules, invents no taxonomy values and mutates nothing.
 */

import { NON_SPENDING_CATEGORIES } from './taxonomyExpense.js';

// Money that arrives as a `type='income'` row but is borrowed, not earned.
// 'Loan Received' is the canonical label; the rest are legacy/importer
// spellings seen in real data (the dashboard hook previously used ONLY the
// legacy spellings, so it excluded nothing at all — the live bug).
export const BORROWING_INCOME_CATEGORIES = new Set([
  'Loan Received',
  'Loan Disbursement',
  'Borrowing',
  'Cash Advance',
  'Debt',
]);

// Incoming money that reverses earlier spending. Never income.
export const REFUND_CATEGORIES = new Set([
  'Refund',
  'Refunds & Reversals',
  'Cashback',
  'Failed Payment Reversal',
  'Vendor Refund',
  'Service Refund',
  'Reimbursement',
]);

// Non-spending purposes come from the canonical taxonomy (one shared list).
export { NON_SPENDING_CATEGORIES };

const typeOf = (t) => String(t?.type || '').trim().toLowerCase();
const catOf = (t) => String(t?.category || '').trim();
const magOf = (t) => Math.abs(Number(t?.amount) || 0);

// Case-folded lookups: taxonomy labels are canonical Title Case, but importer
// and manual rows are not always. Matching case-insensitively keeps ONE rule
// while refusing to let 'loan received' slip through as income.
const BORROWING_FOLDED = new Set([...BORROWING_INCOME_CATEGORIES].map((c) => c.toLowerCase()));
const REFUND_FOLDED = new Set([...REFUND_CATEGORIES].map((c) => c.toLowerCase()));

// BUG-002 — loan drawdowns that bypass the category check. The engine never
// assigns 'Loan Received' to a CREDIT, so a disbursement arrives as plain
// 'Income' and the category test above never fires. Narrow narration test,
// income rows only. Mirror of backend/lib/interim_policy.py — the patterns
// must stay identical (the parity test compares both on a shared corpus).
const BORROW_STANDALONE = /\bCASH\s+ADVANCE\b/i;
const BORROW_TOKEN = /\bLOANS?\b|\bLAP\b|\bBNPL\b|\bOVERDRAFT\b/i;
const STRONG_PROCEEDS = /\bDISB\w*|\bDRAW\s?DOWN\b|\bSANCTION\w*/i;
const PROCEEDS_TOKEN = /\bDISB\w*|\bDRAW\s?DOWN\b|\bSANCTION\w*|\bPROCEEDS\b|\bCREDIT\b|\bCR\b|\bRECEIVED\b|\bAVAILED\b|\bTOP\s?-?\s?UP\b/i;
const EARNED_TOKEN = /\bSALAR\w*|\bPAYROLL\b|\bWAGES?\b|\bPENSION\b|\bDIVIDEND\b|\bINTEREST\b|\bREFUND\b|\bREIMBURS\w*|\bRENT\b|\bBONUS\b|\bCOMMISSION\b|\bSTIPEND\b/i;

/** True when a credit's narration unambiguously describes borrowed money. */
export function isBorrowingNarration(description) {
  const desc = String(description || '').trim();
  if (!desc) return false;
  if (BORROW_STANDALONE.test(desc)) return true;
  if (!BORROW_TOKEN.test(desc)) return false;
  if (STRONG_PROCEEDS.test(desc)) return true;
  if (EARNED_TOKEN.test(desc)) return false;
  return PROCEEDS_TOKEN.test(desc);
}

export function isBorrowingIncome(txn) {
  if (typeOf(txn) !== 'income') return false;
  if (BORROWING_FOLDED.has(catOf(txn).toLowerCase())) return true;
  return isBorrowingNarration(txn?.description || txn?.narration);
}

export function isRefund(txn) {
  if (typeOf(txn) === 'refund') return true;
  return typeOf(txn) === 'income' && REFUND_FOLDED.has(catOf(txn).toLowerCase());
}

// Cash direction vs economic type. A credit proves money entered the account;
// it does NOT prove income. Income needs evidence: the user confirmed it (or
// their own rule did), or the engine assigned a real income category from the
// narration. A credit sitting in Miscellaneous has no evidence at all, and a
// bare credit from a known lender is never income either — both are held as
// UNRESOLVED INFLOWS awaiting confirmation. Mirror of
// backend/lib/interim_policy.py; keep the sets and patterns identical.
export const CONFIRMED_SOURCES = new Set([
  'user_confirmed', 'user_propagated', 'user', 'user_edit',
  'merchant_rule', 'merchant_rules', 'personal_rule', 'rule',
]);

export const WEAK_CATEGORIES = new Set([
  '', 'miscellaneous', 'misc', 'uncategorized', 'uncategorised',
  'unknown', 'other', 'needs review', 'needs_review', 'unresolved',
]);

const KNOWN_LENDER = /\bNAVI\b|\bKREDIT\s?BEE\b|\bCREDIT\s?BEE\b|\bMONEY\s?VIEW\b|\bPAYSENSE\b|\bCASHE\b|\bMPOKKET\b|\bEARLY\s?SALARY\b|\bFIBE\b|\bDHANI\b|\bINDIALENDS\b|\bLAZYPAY\b|\bZESTMONEY\b|\bSMARTCOIN\b|\bKISSHT\b|\bFLEXSALARY\b|\bINCRED\b|\bPOONAWALLA\b|\bLOANTAP\b|\bRUPEEK\b|\bAXIO\b|\bBAJAJ\s+FIN\w*|\bHDB\s+FIN\w*|\bTATA\s+CAPITAL\b|\bFULLERTON\b|\bHERO\s+FINCORP\b|\bIIFL\b|\bMUTHOOT\b|\bMANAPPURAM\b/i;

export function isKnownLenderCredit(description) {
  return KNOWN_LENDER.test(String(description || ''));
}

export function isUserConfirmed(txn) {
  const source = String(txn?.category_source || '').trim().toLowerCase();
  return CONFIRMED_SOURCES.has(source) && !WEAK_CATEGORIES.has(catOf(txn).toLowerCase());
}

export function hasIncomeEvidence(txn) {
  if (isUserConfirmed(txn)) return true;
  if (isKnownLenderCredit(txn?.description || txn?.narration)) return false;
  return !WEAK_CATEGORIES.has(catOf(txn).toLowerCase());
}

/** Money in, meaning unknown: excluded from income, held for review. */
export function isUnresolvedInflow(txn) {
  if (typeOf(txn) !== 'income') return false;
  if (isRefund(txn) || isBorrowingIncome(txn)) return false;
  return !hasIncomeEvidence(txn);
}

/** Real income: a credit that is not borrowed, not a refund, and has
 *  evidence that it is actually income. */
export function isRealIncome(txn) {
  return typeOf(txn) === 'income' && !isBorrowingIncome(txn) && !isRefund(txn)
    && hasIncomeEvidence(txn);
}

export const CLASS = {
  EARNED_INCOME: 'earned_income',
  BORROWED: 'borrowed',
  UNRESOLVED_INFLOW: 'unresolved_inflow',
  SPENDING: 'spending',
  REFUND: 'refund',
  TRANSFER: 'transfer',
  INVESTMENT: 'investment',
  OTHER: 'other',
};

export function classify(txn) {
  if (isRefund(txn)) return CLASS.REFUND;
  const t = typeOf(txn);
  if (t === 'income') {
    if (isBorrowingIncome(txn)) return CLASS.BORROWED;
    if (!hasIncomeEvidence(txn)) return CLASS.UNRESOLVED_INFLOW;
    return CLASS.EARNED_INCOME;
  }
  if (t === 'expense') {
    // A row filed under a non-spending purpose (Investments, Transfers) is money
    // moved, not money spent — even when the importer typed it as an expense.
    const cat = catOf(txn);
    if (NON_SPENDING_CATEGORIES.has(cat)) {
      return cat === 'Investments' ? CLASS.INVESTMENT : CLASS.TRANSFER;
    }
    return CLASS.SPENDING;
  }
  if (t === 'transfer') return CLASS.TRANSFER;
  if (t === 'investment') return CLASS.INVESTMENT;
  return CLASS.OTHER;
}

/**
 * One period's totals plus what was excluded.
 * `amountOf` lets callers convert to base currency before the sum
 * (the dashboard passes a CurrencyContext-backed converter).
 */
export function periodTotals(txns, amountOf = magOf) {
  let income = 0, grossExpenses = 0, refunds = 0, borrowed = 0, transfers = 0, investments = 0;
  let unresolved = 0;
  let nBorrowed = 0, nRefund = 0, nTransfer = 0, nInvestment = 0, nUnresolved = 0;

  for (const t of txns || []) {
    const amt = amountOf(t);
    switch (classify(t)) {
      case CLASS.EARNED_INCOME: income += amt; break;
      case CLASS.BORROWED:      borrowed += amt; nBorrowed += 1; break;
      case CLASS.UNRESOLVED_INFLOW: unresolved += amt; nUnresolved += 1; break;
      case CLASS.SPENDING:      grossExpenses += amt; break;
      case CLASS.REFUND:        refunds += amt; nRefund += 1; break;
      case CLASS.TRANSFER:      transfers += amt; nTransfer += 1; break;
      case CLASS.INVESTMENT:    investments += amt; nInvestment += 1; break;
      default: break;
    }
  }

  const expenses = grossExpenses - refunds;
  const savings = income - expenses;

  return {
    income,
    grossExpenses,
    refunds,
    expenses,
    savings,
    savingsRatePct: income > 0 ? (savings / income) * 100 : null,
    investments,
    excludedCounts: {
      borrowedIncome: nBorrowed,
      borrowedIncomeAmount: borrowed,
      unresolvedInflow: nUnresolved,
      unresolvedInflowAmount: unresolved,
      refunds: nRefund,
      refundsAmount: refunds,
      transfers: nTransfer,
      transfersAmount: transfers,
      investments: nInvestment,
      investmentsAmount: investments,
    },
  };
}

/** Sum the excluded_counts of several periods into one disclosure object. */
export function mergeExcluded(list) {
  const out = {
    borrowedIncome: 0, borrowedIncomeAmount: 0,
    unresolvedInflow: 0, unresolvedInflowAmount: 0,
    refunds: 0, refundsAmount: 0,
    transfers: 0, transfersAmount: 0,
    investments: 0, investmentsAmount: 0,
  };
  for (const e of list || []) {
    if (!e) continue;
    for (const k of Object.keys(out)) out[k] += (e[k] || 0);
  }
  return out;
}
